// HardwareOverview.tsx
import { useState, useCallback, useEffect } from "react";
import {
    RefreshCw, Loader2, Cpu, MemoryStick, HardDrive,
    ShieldAlert, Info
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";


// ── Types ─────────────────────────────────────────────────────────────────────
interface CpuData {
    idle_pct: number;
    user_pct: number;
    core_count: number;
    system_pct: number;
    current_pct: number;
    description: string;
    "1min_avg_pct": number;
    "1min_max_pct": number;
    "1min_min_pct": number;
}

interface DiskData {
    partitions: any[];
    description: string;
}

interface MemoryData {
    used_mb: number;
    total_mb: number;
    used_pct: number;
    description: string;
}

interface EndOfLifeData {
    note: string;
    model: string;
    description: string;
    support_level: string;
    end_of_sale_date: string;
    firmware_version: string;
    end_of_support_date: string;
}

interface HardwareData {
    cpu: CpuData;
    disk: DiskData;
    memory: MemoryData;
    end_of_life: EndOfLifeData;
}

// ── Gauge Ring ────────────────────────────────────────────────────────────────
const GaugeRing = ({ pct, size = 80, stroke = 7, color }: {
    pct: number; size?: number; stroke?: number; color: string;
}) => {
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const filled = (pct / 100) * circ;
    return (
        <svg width={size} height={size} className="-rotate-90">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke={color} strokeWidth={stroke}
                strokeDasharray={`${filled} ${circ}`}
                strokeLinecap="round"
                style={{ transition: "stroke-dasharray 0.8s ease" }}
            />
        </svg>
    );
};

// ── Stat Pill ─────────────────────────────────────────────────────────────────
const StatPill = ({ label, value, color = "text-gray-300" }: { label: string; value: string | number; color?: string }) => (
    <div className="flex flex-col items-center gap-0.5 px-3 py-2 bg-gray-900/60 border border-gray-700/50 rounded-lg">
        <span className={`text-[13px] font-black ${color}`}>{value}</span>
        <span className="text-[8px] font-bold text-gray-200 uppercase tracking-widest">{label}</span>
    </div>
);

// ── Progress Bar ──────────────────────────────────────────────────────────────
const ProgressBar = ({ pct, color }: { pct: number; color: string }) => (
    <div className="w-full h-1.5 bg-gray-700/50 rounded-full overflow-hidden">
        <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${Math.min(pct, 100)}%`, background: color }}
        />
    </div>
);

// ── Color by threshold ────────────────────────────────────────────────────────
const getPctColor = (pct: number): { ring: string; text: string; bar: string } => {
    if (pct >= 85) return { ring: "#ef4444", text: "text-red-400", bar: "#ef4444" };
    if (pct >= 65) return { ring: "#f97316", text: "text-orange-400", bar: "#f97316" };
    if (pct >= 40) return { ring: "#eab308", text: "text-yellow-400", bar: "#eab308" };
    return { ring: "#22c55e", text: "text-green-400", bar: "#22c55e" };
};

// ── Section Header ────────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, label, color }: { icon: any; label: string; color: string }) => (
    <div className={`flex items-center gap-2 mb-3`}>
        <div className={`p-1.5 rounded-lg border ${color}`}>
            <Icon size={13} className={color.includes("blue") ? "text-blue-400" : color.includes("purple") ? "text-purple-400" : color.includes("cyan") ? "text-cyan-400" : "text-amber-400"} />
        </div>
        <h3 className={`text-[11px] font-black uppercase tracking-widest ${color.includes("blue") ? "text-blue-400" : color.includes("purple") ? "text-purple-400" : color.includes("cyan") ? "text-cyan-400" : "text-amber-400"}`}>
            {label}
        </h3>
    </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
export default function HardwareOverview({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [data, setData] = useState<HardwareData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "hardware",
                    }),
                    limit: 1,
                },
            });
            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                setData(parsed);
            } else showError("Failed to load hardware data");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return (
        <div className="flex items-center justify-center gap-2 py-20">
            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">Loading hardware data...</span>
        </div>
    );

    if (!data) return (
        <div className="flex flex-col items-center gap-2 py-16 opacity-40">
            <Info size={22} className="text-gray-200" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">No hardware data available</span>
        </div>
    );

    const cpuColor = getPctColor(data.cpu.current_pct);
    const memColor = getPctColor(data.memory.used_pct);

    return (
        <div className="space-y-4">

            {/* Top bar */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-600/20 p-1.5 rounded-lg border border-blue-500/30">
                        <Cpu size={15} className="text-blue-400" />
                    </div>
                    <h1 className="text-[14px] font-black text-white tracking-wide">Hardware Overview</h1>
                    <span className="px-2 py-0.5 text-[9px] font-bold bg-gray-800 border border-gray-700 text-gray-200 rounded uppercase tracking-widest">
                        {data.end_of_life.model} · {data.end_of_life.firmware_version}
                    </span>
                </div>
                <button
                    onClick={fetchData}
                    className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group"
                >
                    <RefreshCw size={14} className="group-hover:text-blue-400" />
                </button>
            </div>

            {/* Cards Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">

                {/* ── CPU Card ── */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 col-span-1 md:col-span-2">
                    <SectionHeader icon={Cpu} label="CPU" color="bg-blue-600/20 border-blue-500/30" />

                    <div className="flex items-center gap-6">
                        {/* Gauge */}
                        <div className="relative shrink-0">
                            <GaugeRing pct={data.cpu.current_pct} size={96} stroke={8} color={cpuColor.ring} />
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className={`text-[18px] font-black leading-none ${cpuColor.text}`}>
                                    {data.cpu.current_pct}%
                                </span>
                                <span className="text-[8px] text-gray-200 uppercase tracking-widest font-bold mt-0.5">Live</span>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-3 gap-2">
                                <StatPill label="Cores" value={data.cpu.core_count} color="text-blue-300" />
                                <StatPill label="User" value={`${data.cpu.user_pct}%`} color="text-purple-300" />
                                <StatPill label="System" value={`${data.cpu.system_pct}%`} color="text-cyan-300" />
                                <StatPill label="Idle" value={`${data.cpu.idle_pct}%`} color="text-green-300" />
                                <StatPill label="1m Avg" value={`${data.cpu["1min_avg_pct"]}%`} color="text-yellow-300" />
                                <StatPill label="1m Max" value={`${data.cpu["1min_max_pct"]}%`} color="text-orange-300" />
                            </div>

                            {/* 1-min sparkline bar */}
                            <div className="space-y-1">
                                <div className="flex justify-between items-center">
                                    <span className="text-[8px] font-bold text-gray-200 uppercase tracking-widest">1-Min Range</span>
                                    <span className="text-[9px] text-gray-200 font-mono">
                                        {data.cpu["1min_min_pct"]}% – {data.cpu["1min_max_pct"]}%
                                    </span>
                                </div>
                                <div className="relative h-2 bg-gray-600/50 rounded-full overflow-hidden">
                                    {/* range band */}
                                    <div
                                        className="absolute h-full rounded-full"
                                        style={{
                                            left: `${data.cpu["1min_min_pct"]}%`,
                                            width: `${data.cpu["1min_max_pct"] - data.cpu["1min_min_pct"]}%`,
                                            background: cpuColor.ring,
                                        }}
                                    />
                                    {/* avg marker */}
                                    <div
                                        className="absolute top-0 h-full w-0.5 rounded-full"
                                        style={{ left: `${data.cpu["1min_avg_pct"]}%`, background: cpuColor.ring }}
                                    />
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-[8px] text-gray-200">Min</span>
                                    <span className="text-[8px] text-gray-200">Avg: {data.cpu["1min_avg_pct"]}%</span>
                                    <span className="text-[8px] text-gray-200">Max</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Usage breakdown bar */}
                    <div className="mt-4 space-y-1.5">
                        <div className="flex justify-between text-[8px] font-bold text-gray-200 uppercase tracking-widest">
                            <span>Usage Breakdown</span>
                            <span>{data.cpu.user_pct + data.cpu.system_pct}% active</span>
                        </div>
                        <div className="h-2 w-full bg-gray-700/50 rounded-full overflow-hidden flex">
                            <div className="h-full bg-purple-500/70" style={{ width: `${data.cpu.user_pct}%`, transition: "width 0.7s" }} title={`User ${data.cpu.user_pct}%`} />
                            <div className="h-full bg-cyan-500/70" style={{ width: `${data.cpu.system_pct}%`, transition: "width 0.7s" }} title={`System ${data.cpu.system_pct}%`} />
                            <div className="h-full bg-gray-600/30" style={{ width: `${data.cpu.idle_pct}%`, transition: "width 0.7s" }} title={`Idle ${data.cpu.idle_pct}%`} />
                        </div>
                        <div className="flex gap-3">
                            {[
                                { label: "User", color: "bg-purple-500/70" },
                                { label: "System", color: "bg-cyan-500/70" },
                                { label: "Idle", color: "bg-gray-600/30" },
                            ].map(({ label, color }) => (
                                <div key={label} className="flex items-center gap-1">
                                    <div className={`w-2 h-2 rounded-sm ${color}`} />
                                    <span className="text-[8px] text-gray-200">{label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Memory Card ── */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                    <SectionHeader icon={MemoryStick} label="Memory" color="bg-purple-600/20 border-purple-500/30" />

                    <div className="flex flex-col items-center gap-4">
                        {/* Gauge */}
                        <div className="relative">
                            <GaugeRing pct={data.memory.used_pct} size={100} stroke={9} color={memColor.ring} />
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className={`text-[20px] font-black leading-none ${memColor.text}`}>
                                    {data.memory.used_pct}%
                                </span>
                                <span className="text-[8px] text-gray-200 uppercase tracking-widest font-bold mt-0.5">Used</span>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="w-full space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                                <StatPill label="Used" value={`${data.memory.used_mb.toFixed(0)} MB`} color="text-purple-300" />
                                <StatPill label="Total" value={`${data.memory.total_mb.toFixed(0)} MB`} color="text-gray-300" />
                            </div>
                            <div className="space-y-1">
                                <div className="flex justify-between text-[8px] text-gray-200 font-bold uppercase tracking-widest">
                                    <span>Free</span>
                                    <span>{(data.memory.total_mb - data.memory.used_mb).toFixed(0)} MB</span>
                                </div>
                                <ProgressBar pct={data.memory.used_pct} color={memColor.bar} />
                            </div>
                        </div>

                        {/* Warning */}
                        {data.memory.used_pct >= 85 && (
                            <div className="w-full flex items-center gap-1.5 px-2 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg">
                                <ShieldAlert size={11} className="text-red-400 shrink-0" />
                                <span className="text-[9px] text-red-400 font-bold">High usage — session drops possible</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Disk Card ── */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                    <SectionHeader icon={HardDrive} label="Disk" color="bg-cyan-600/20 border-cyan-500/30" />

                    {data.disk.partitions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-3 py-8">
                            <div className="w-12 h-12 rounded-xl bg-gray-700/40 border border-gray-600/30 flex items-center justify-center">
                                <HardDrive size={20} className="text-gray-200" />
                            </div>
                            <div className="text-center">
                                <p className="text-[10px] font-bold text-gray-200">No local partitions</p>
                                <p className="text-[9px] text-gray-200 mt-0.5">Used for firmware, logs &amp; quarantine</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {data.disk.partitions.map((p: any, i: number) => {
                                const color = getPctColor(p.used_pct ?? 0);
                                return (
                                    <div key={i} className="space-y-1">
                                        <div className="flex justify-between text-[9px]">
                                            <span className="text-gray-300 font-bold">{p.name ?? `Partition ${i + 1}`}</span>
                                            <span className={color.text}>{p.used_pct ?? 0}%</span>
                                        </div>
                                        <ProgressBar pct={p.used_pct ?? 0} color={color.bar} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ── End of Life Card ── */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <SectionHeader icon={ShieldAlert} label="Product Lifecycle" color="bg-amber-600/20 border-amber-500/30" />

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[
                        { label: "Model", value: data.end_of_life.model },
                        { label: "Firmware", value: data.end_of_life.firmware_version },
                        { label: "Support Level", value: data.end_of_life.support_level || "—" },
                        { label: "End of Sale", value: data.end_of_life.end_of_sale_date || "—" },
                        { label: "End of Support", value: data.end_of_life.end_of_support_date || "—" },
                    ].map(({ label, value }) => (
                        <div key={label} className="flex flex-col gap-1 px-3 py-2.5 bg-gray-900/50 border border-gray-700/50 rounded-lg">
                            <span className="text-[8px] font-bold text-gray-200 uppercase tracking-widest">{label}</span>
                            <span className={`text-[12px] font-black ${value === "—" ? "text-gray-200" : "text-white"}`}>
                                {value}
                            </span>
                        </div>
                    ))}
                </div>

                {data.end_of_life.note && (
                    <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                        <Info size={11} className="text-amber-400 shrink-0 mt-0.5" />
                        <p className="text-[9px] text-amber-400/80">{data.end_of_life.note}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
